import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { apiKeys, type ApiKey, type ApiKeyCreated } from "@/api/apiKeys";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
} from "@/components/ui";

const SCOPES = [
  "read:portfolio",
  "read:orders",
  "read:fills",
  "read:market",
  "write:orders",
  "write:withdrawals",
  "admin:full",
];

export function ApiKeysPage() {
  const list = useQuery({
    queryKey: ["my-api-keys"],
    queryFn: () => apiKeys.list(),
  });
  const [showForm, setShowForm] = useState(false);
  const [newlyCreated, setNewlyCreated] = useState<ApiKeyCreated | null>(null);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-term-fg">API keys</h1>
          <p className="text-2xs text-term-mute">
            Programmatic access for headless clients. The plaintext key is
            shown <strong>once</strong> at creation — store it in a password
            manager. See{" "}
            <a className="text-term-accent underline" href="/docs">
              /docs
            </a>{" "}
            (Swagger UI) or{" "}
            <a className="text-term-accent underline" href="/redoc">
              /redoc
            </a>{" "}
            for API reference.
          </p>
        </div>
        <Button onClick={() => { setShowForm((x) => !x); setNewlyCreated(null); }}>
          {showForm ? "Hide form" : "Create key"}
        </Button>
      </div>

      {newlyCreated && <NewKeyBanner created={newlyCreated} onDismiss={() => setNewlyCreated(null)} />}
      {showForm && !newlyCreated && (
        <CreateKeyForm
          onCreated={(k) => { setNewlyCreated(k); setShowForm(false); }}
        />
      )}

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <p className="p-3 text-sm text-term-mute">Loading…</p>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState
              title="No API keys yet"
              description="Click 'Create key' to mint your first programmatic access key."
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Name</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Prefix</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Scopes</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Created</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Expires</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Last used</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.data.map((k: ApiKey) => (
                  <ApiKeyRow key={k.id} k={k} />
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function ApiKeyRow({ k }: { k: ApiKey }) {
  const qc = useQueryClient();
  const toast = useToast();
  const revoke = useMutation({
    mutationFn: () => apiKeys.revoke(k.id),
    onSuccess: () => {
      toast.push("Key revoked", "success");
      qc.invalidateQueries({ queryKey: ["my-api-keys"] });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "revoke failed";
      toast.push(msg, "error");
    },
  });
  const status = k.revoked_at
    ? "revoked"
    : k.expires_at && new Date(k.expires_at) < new Date()
    ? "expired"
    : "active";
  return (
    <tr className="border-t border-term-border">
      <td className="px-3 py-1.5 text-term-fg">{k.name}</td>
      <td className="px-3 py-1.5 font-mono text-term-dim">{k.prefix}…</td>
      <td className="px-3 py-1.5">
        {k.scopes.map((s) => (
          <span key={s} className="mr-1 inline-block">
            <Badge>{s}</Badge>
          </span>
        ))}
      </td>
      <td className="px-3 py-1.5 text-term-dim">
        {new Date(k.created_at).toLocaleDateString()}
      </td>
      <td className="px-3 py-1.5 text-term-dim">
        {k.expires_at ? new Date(k.expires_at).toLocaleDateString() : "never"}
      </td>
      <td className="px-3 py-1.5 text-term-dim">
        {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "—"}
      </td>
      <td className="px-3 py-1.5">
        <Badge>{status}</Badge>
      </td>
      <td className="px-3 py-1.5 text-right">
        {status === "active" && (
          <Button
            variant="danger"
            onClick={() => {
              if (confirm("Revoke this key? Clients using it will get 401 on next call.")) {
                revoke.mutate();
              }
            }}
          >
            Revoke
          </Button>
        )}
      </td>
    </tr>
  );
}

function CreateKeyForm({ onCreated }: { onCreated: (k: ApiKeyCreated) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>(["read:portfolio"]);
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => apiKeys.create({ name, scopes }),
    onSuccess: (k) => {
      qc.invalidateQueries({ queryKey: ["my-api-keys"] });
      onCreated(k);
      toast.push("Key created", "success");
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "create failed";
      setError(msg);
    },
  });
  return (
    <Card>
      <CardBody className="space-y-2">
        <div>
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="my-trading-bot" />
        </div>
        <div>
          <Label>Scopes</Label>
          <div className="flex flex-wrap gap-2 text-2xs">
            {SCOPES.map((s) => (
              <label key={s} className="flex items-center gap-1 text-term-dim">
                <input
                  type="checkbox"
                  checked={scopes.includes(s)}
                  onChange={(e) => {
                    setScopes((cur) =>
                      e.target.checked ? [...cur, s] : cur.filter((x) => x !== s),
                    );
                  }}
                />
                {s}
              </label>
            ))}
          </div>
        </div>
        {error && <FormError>{error}</FormError>}
        <Button
          onClick={() => {
            if (!name.trim()) {
              setError("Name is required");
              return;
            }
            setError(null);
            create.mutate();
          }}
          loading={create.isPending}
        >
          Create
        </Button>
      </CardBody>
    </Card>
  );
}

function NewKeyBanner({
  created,
  onDismiss,
}: {
  created: ApiKeyCreated;
  onDismiss: () => void;
}) {
  return (
    <Card>
      <CardBody className="space-y-2 border-l-2 border-term-accent">
        <p className="text-sm font-semibold text-term-fg">Key created — copy it now</p>
        <p className="text-2xs text-term-mute">
          This is the only time the plaintext key is shown. We store only a hash;
          if you lose this you'll need to revoke and create a new key.
        </p>
        <Input value={created.plaintext} readOnly className="font-mono text-xs" />
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={() => navigator.clipboard.writeText(created.plaintext)}
          >
            Copy
          </Button>
          <Button variant="ghost" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
